import './App.css';
import {
  Box,
  Center,
  ChakraProvider,
  Flex,
  Image,
  Text,
  useDisclosure,
  useMediaQuery,
} from '@chakra-ui/react';

import VoiceChatWindow from './components/voice-chat/VoiceChatWindow';
import { useState, useEffect } from 'react';
import OpenAI from './components/chat/bots/OpenAI';
import LanguageSelector from './components/chat/languageSelector/languageSelector';
import CopilotLogo from '../src/assets/images/copilot.png';
import TUFooterLogo from '../src/assets/images/tu-footer-logo.png';
import i18n from './i18n';
import { useTranslation } from 'react-i18next';

function App() {
  const isIOS = useMediaQuery('(pointer: fine)');
  const { t } = useTranslation();

  const {
    isOpen: isOpenAiVoiceChatOpen,
    onClose: onOpenAiVoiceVoiceChatClose,
    onOpen: onOpenAiVoiceVoiceChatOpen,
  } = useDisclosure();

  const [conversationId, setConversationId] = useState<string | null>(null);
  const [language, setLanguage] = useState<'EN' | 'AR'>('AR');
  const [hideLanguageSwitch, setHideLanguageSwitch] = useState(false);
  const [selectedVoice, setSelectedVoice] = useState<string>('');

  const generateGUID = () => {
    return 'xxxxxxxx-xxxx-4xxx-yxxx-xxxxxxxxxxxx'.replace(
      /[xy]/g,
      function (c) {
        var r = (Math.random() * 16) | 0,
          v = c === 'x' ? r : (r & 0x3) | 0x8;
        return v.toString(16);
      }
    );
  };

  useEffect(() => {
    if (!conversationId) {
      setConversationId(generateGUID());
    }
  }, [conversationId]);

  const handleLanguageChange = (newLanguage: 'EN' | 'AR') => {
    setLanguage(newLanguage);
    i18n.changeLanguage(newLanguage.toLowerCase());
  };

  return (
    <ChakraProvider>
      <Box dir={language === 'AR' ? 'rtl' : 'ltr'} overflow="hidden">
        <Center
          bg="#353535"
          h={{ base: '8vh', md: '12vh' }}
          justifyContent="center"
          px={{ base: '2rem', md: '25vh', '2xl': '35vh' }}
        >
          <Image src="./logo-lg.png" h={{ base: '3rem', md: '4rem' }} />
        </Center>
        <Box
          mx="auto"
          pos="relative"
          h={{ base: 'calc(85vh - 4.5em)', md: '84vh' }}
        >
          <Box px={{ base: '1rem', md: '25vh', '2xl': '35vh' }} mx="auto">
            {!hideLanguageSwitch && (
              <Center
                position="absolute"
                top={{ base: '30%', md: '25%' }}
                left="50%"
                transform="translateX(-50%)"
                zIndex="1"
              >
                <LanguageSelector
                  selectedLanguage={language}
                  onLanguageChange={handleLanguageChange}
                />
              </Center>
            )}
            {conversationId && (
              <OpenAI
                conversationId={conversationId}
                openVoiceChat={onOpenAiVoiceVoiceChatOpen}
                language={language}
                setHideLanguageSwitch={setHideLanguageSwitch}
                setSelectedVoice={setSelectedVoice}
              />
            )}

            {isOpenAiVoiceChatOpen && (
              <Box h="full" w="full">
                {conversationId && (
                  <VoiceChatWindow
                    conversationId={conversationId}
                    onClose={onOpenAiVoiceVoiceChatClose}
                    selectedVoice={selectedVoice}
                    isArabic={language === 'AR' ? true : false}
                  />
                )}
              </Box>
            )}
          </Box>
        </Box>
      </Box>
      {/* Footer */}
      <Box
        h={{ base: '4vh', md: '4vh' }}
        display="flex"
        alignItems="center"
        pos="relative"
        zIndex={0}
        bg="#353535"
        color="white"
      >
        <Center
          display="flex"
          pos="absolute"
          w="full"
          justifyContent="center"
          dir={language === 'AR' ? 'rtl' : 'ltr'}
        >
          <Text fontSize="xs" me="0.4rem">
            {t('poweredBy')}
          </Text>
          <Image src={CopilotLogo} h="13px" />
          <Text overflow="hidden" h={{ base: '20px', md: '28px' }} px="1.5">
            |
          </Text>
          <Image src={TUFooterLogo} h="10px" />
        </Center>
      </Box>
    </ChakraProvider>
  );
}

export default App;
